// SelectPill.jsx
// Client: vmfive
// Version: 1.1.0
// Fixed-width, color-coded pill that opens a popover menu on click.
// Drives both Application status AND integration type — value can change
// without the control changing width (zero layout reflow).
//
// B-1 fix: popover is rendered via ReactDOM.createPortal into document.body
// with position:fixed + JS-computed coordinates so it cannot be clipped by
// any ancestor overflow:hidden and is unaffected by ancestor CSS transforms
// (e.g. Drawer's translateX).  Scroll/resize events close the popover.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Icon from './Icon';
import useDismiss from './useDismiss';
import './components-company-application.css';

/**
 * SelectPill — props:
 *   value       {string}   Current value key (e.g. 'active', 'gam')
 *   options     {Array}    [{ value, label, dot?, danger? }]
 *   onChange    {Function} Called with new value string on selection
 *   disabled    {boolean}  Opacity 0.55 + not-allowed, no popover
 *   headLabel   {string=}  Optional uppercase group label shown at top of popover
 *   className   {string=}  Extra CSS class on the wrapper span
 *
 * Variant classes applied automatically from VARIANT_MAP:
 *   sp--active / sp--paused / sp--archived / sp--gam / sp--hardcode / sp--unset
 */

const VARIANT_MAP = {
  active:   'sp--active',
  paused:   'sp--paused',
  archived: 'sp--archived',
  gam:      'sp--gam',
  hardcode: 'sp--hardcode',
  unset:    'sp--unset',
  '':       'sp--unset',
};

export default function SelectPill({
  value,
  options = [],
  onChange,
  disabled = false,
  headLabel,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  // Fixed viewport position for the portal popover
  const [popPos, setPopPos] = useState({ top: 0, right: 0 });

  const close = useCallback(() => setOpen(false), []);
  const [triggerRef, popRef] = useDismiss(open, close);

  const variantClass = VARIANT_MAP[value] || 'sp--paused';

  const currentOption = options.find((o) => o.value === value);
  const label = currentOption ? currentOption.label : value;

  // Compute popover position from the trigger's viewport rect
  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPopPos({
      top: rect.bottom + 6,
      right: window.innerWidth - rect.right,
    });
  }, [triggerRef]);

  const handleTriggerClick = (e) => {
    e.stopPropagation();
    if (disabled) return;
    if (!open) {
      computePosition();
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleItemClick = (e, optValue) => {
    e.stopPropagation();
    setOpen(false);
    if (optValue !== value && onChange) {
      onChange(optValue);
    }
  };

  // Close on scroll (any scrollable container) or resize
  useEffect(() => {
    if (!open) return;
    const handleClose = () => setOpen(false);
    window.addEventListener('scroll', handleClose, { capture: true, passive: true });
    window.addEventListener('resize', handleClose, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleClose, { capture: true });
      window.removeEventListener('resize', handleClose);
    };
  }, [open]);

  const popover = open
    ? ReactDOM.createPortal(
        <div
          ref={popRef}
          className="pop pop--fixed"
          role="listbox"
          aria-label={headLabel || 'Options'}
          style={{ top: popPos.top, right: popPos.right }}
        >
          {headLabel && <div className="pop__head">{headLabel}</div>}
          {options.map((opt) => {
            const isCurrent = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isCurrent}
                className={`pop__item${isCurrent ? ' is-current' : ''}${opt.danger ? ' danger' : ''}`}
                onClick={(e) => handleItemClick(e, opt.value)}
              >
                {opt.dot && (
                  <span className="pop__dot" style={{ background: opt.dot }} />
                )}
                {opt.label}
                {isCurrent && (
                  <span className="pop__check">
                    <Icon name="check" size={14} strokeWidth={1.8} />
                  </span>
                )}
              </button>
            );
          })}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <span
        ref={triggerRef}
        className={`select-pill-wrap ${className}`}
        style={{ position: 'relative', width: '100%', display: 'inline-block' }}
      >
        <button
          type="button"
          className={`select-pill ${variantClass}`}
          aria-expanded={open ? 'true' : 'false'}
          aria-haspopup="listbox"
          disabled={disabled}
          onClick={handleTriggerClick}
        >
          <span className="select-pill__left">
            <span className="select-pill__dot" />
            <span className="select-pill__label">{label}</span>
          </span>
          {!disabled && (
            <span className="select-pill__caret">
              <Icon name="chevron-down" size={13} strokeWidth={1.8} />
            </span>
          )}
        </button>
      </span>
      {popover}
    </>
  );
}
